import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "../services/api";
import {
  fetchAvailability,
  newIdempotencyKey,
  rescheduleAppointment,
} from "../services/bookingService";
import { fetchProviders } from "../services/providerService";
import { formatDateTime, inZone } from "../services/datetime";

/**
 * Move a booking to a different time or provider.
 *
 * The times offered are real availability for the booking's *first* service —
 * asked of the engine with this appointment excluded, so its current slot is
 * not counted as a clash with itself.
 *
 * A multi-service visit moves as a whole: the server keeps each line's offset
 * from the start, so a cut followed by a colour stays in that order. Only the
 * first line's availability is shown, which is what a receptionist is choosing.
 *
 * OUP-39 — the provider can be changed here too, not only the time. Picking one
 * re-asks the engine for *their* availability, so the times offered always
 * belong to whoever is selected. The list is the eligible set for this branch
 * and this service; the engine refuses anybody else, and a name in the list
 * that cannot take the booking is a trap.
 */
export default function RescheduleModal({ open, appointment, onClose, onRescheduled }) {
  const { t, i18n } = useTranslation();

  const [date, setDate] = useState("");
  const [staffId, setStaffId] = useState("");
  const [slots, setSlots] = useState([]);
  const [providers, setProviders] = useState([]);
  const [zone, setZone] = useState("UTC");
  const [chosen, setChosen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [key, setKey] = useState(newIdempotencyKey);

  const firstLine = appointment?.services?.[0];

  useEffect(() => {
    if (!open || !appointment) return;

    setDate(appointment.local_starts_at?.slice(0, 10) ?? "");
    setStaffId(firstLine?.staff_id ?? "");
    setChosen(null);
    setSlots([]);
    setError("");
    setKey(newIdempotencyKey());
  }, [open, appointment, firstLine]);

  // Who may take this booking: at this branch, able to perform this service,
  // and still employed. Asked once per opening.
  useEffect(() => {
    if (!open || !appointment || !firstLine) return;

    fetchProviders({
      perPage: 100,
      filters: {
        branch: appointment.branch_id,
        service: firstLine.service_id,
        employmentStatus: "active",
      },
    })
      .then(({ providers: list }) => setProviders(list))
      .catch(() => setProviders([]));
  }, [open, appointment, firstLine]);

  const loadSlots = useCallback(async () => {
    if (!open || !appointment || !firstLine || !date) return;

    setLoading(true);
    setError("");

    try {
      // Blank means "keep", so the times shown are the current provider's —
      // asking with null would offer slots across everybody and hand back
      // times this booking cannot actually take.
      const { slots: list, meta } = await fetchAvailability(
        appointment.branch_id,
        firstLine.service_id,
        date,
        { staffId: staffId || firstLine.staff_id || null }
      );
      setSlots(list);
      setZone(meta?.timezone ?? appointment.timezone ?? "UTC");
    } catch (err) {
      setSlots([]);
      setError(apiErrorMessage(err, t("appointments.availabilityFailed")));
    } finally {
      setLoading(false);
    }
  }, [open, appointment, firstLine, date, staffId, t]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  if (!open || !appointment) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!chosen) return;

    setSaving(true);
    setError("");

    try {
      const moved = await rescheduleAppointment(
        appointment.id,
        `${date} ${chosen}`,
        staffId || null,
        key
      );
      onRescheduled?.(moved);
      onClose();
    } catch (err) {
      if (err.response?.status === 409) {
        setError(t("appointments.slotTaken"));
        setKey(newIdempotencyKey());
        setChosen(null);
        loadSlots();
      } else {
        setError(apiErrorMessage(err, t("appointments.rescheduleFailed")));
      }
    } finally {
      setSaving(false);
    }
  };

  const currentProvider = firstLine?.staff;
  // Only warn when it is really a change: an untouched select is not one.
  const changingProvider = Boolean(staffId) && staffId !== (firstLine?.staff_id ?? "");

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="card my-8 w-full max-w-lg p-5">
        <h2 className="mb-1 text-lg font-bold">{t("appointments.rescheduleTitle")}</h2>
        <p className="mb-4 text-sm text-ink-2">
          {t("appointments.currentlyAt", {
            time: formatDateTime(appointment.local_starts_at, i18n.language),
          })}
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="field-label" htmlFor="reschedule-date">
              {t("appointments.newDate")}
            </label>
            <input
              id="reschedule-date"
              type="date"
              className="field-input"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setChosen(null);
              }}
            />
          </div>

          <div>
            <label className="field-label" htmlFor="reschedule-staff">
              {t("appointments.provider")}
            </label>
            <select
              id="reschedule-staff"
              className="field-input"
              value={staffId}
              onChange={(e) => {
                setStaffId(e.target.value);
                setChosen(null);
              }}
            >
              {/* Blank means "keep whoever each line already has" — that is
                  what the API reads a null staff_id as, and a multi-service
                  visit may legitimately have a different provider per line.
                  It is NOT "any provider": the write has no way to say that. */}
              <option value="">{t("appointments.keepProvider")}</option>

              {/* The booking's current provider stays listed even if they have
                  since become ineligible, so the select can show who it is
                  now rather than silently reading as "any". */}
              {currentProvider && !providers.some((p) => p.id === currentProvider.id) && (
                <option value={currentProvider.id}>{currentProvider.display_name}</option>
              )}

              {providers.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.display_name}
                </option>
              ))}
            </select>

            {changingProvider && (
              <p className="mt-1 text-xs text-warning-text">
                {t("appointments.providerWillChange")}
              </p>
            )}
          </div>

          <div>
            <p className="field-label">{t("appointments.availableSlots")}</p>

            {loading ? (
              <p className="py-3 text-sm text-ink-2">{t("common.loading")}</p>
            ) : slots.length === 0 ? (
              <p className="rounded-lg border border-warning-line bg-warning-wash px-3 py-2 text-sm text-warning-text">
                {t("appointments.noSlots")}
              </p>
            ) : (
              <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto">
                {slots.map((slot) => {
                  const label = inZone(slot.starts_at, zone).time;
                  const active = chosen === label;

                  return (
                    <button
                      key={slot.starts_at}
                      type="button"
                      onClick={() => setChosen(label)}
                      className={`rounded-lg border px-3 py-1 text-sm ${
                        active
                          ? "border-accent bg-accent-wash text-accent-text"
                          : "border-line text-ink"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {error && <p className="field-error">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving || !chosen}>
              {saving ? t("common.saving") : t("appointments.move")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
